import React, { FC, useState } from 'react'
import { Box, Button, Typography, Stack } from '@mui/material'
import PlayCircleFilledWhiteIcon from '@mui/icons-material/PlayCircleFilledWhite'
import PauseCircleIcon from '@mui/icons-material/PauseCircle'
import MicIcon from '@mui/icons-material/Mic'
import ReplayIcon from '@mui/icons-material/Replay'
import CheckIcon from '@mui/icons-material/Check'
import { legacyTheme as theme } from '@/apps/legacy/shared/styles/theme'
import { colorTokens } from '@/apps/legacy/shared/styles/colorTokens'
import { getRecordingButtonText } from '@/shared/constants/const'
import { localStorageWrapper } from '@/shared/helpers/localStorage'
import { PermissionState } from '../../hooks/useMicrophonePermission'

interface NewRecordButtonProps {
  audioURL: string | null
  audioRef: React.RefObject<HTMLAudioElement>
  isRecording: boolean
  isAutoStopped: boolean
  permissionStatus: PermissionState
  onStartRecording: () => void
  onStopRecording: () => void
  onClearAudio: () => void
  onSendAudio: () => void
}

const LIMIT_MINUTES = 15

export const NewRecordButton: FC<NewRecordButtonProps> = ({
  audioURL,
  audioRef,
  isRecording,
  isAutoStopped,
  permissionStatus,
  onStartRecording,
  onStopRecording,
  onClearAudio,
  onSendAudio,
}) => {
  const [isAudioPlaying, setIsAudioPlaying] = useState(false)
  const isUseNsips = localStorageWrapper.getItem('IsUseNsips') || false

  const handlePlaySound = async () => {
    if (audioRef.current) {
      if (isAudioPlaying) {
        audioRef.current.pause()
        setIsAudioPlaying(false)
      } else {
        await audioRef.current.play()
        setIsAudioPlaying(true)
      }
    }
  }

  if (audioURL) {
    return (
      <Stack>
        <audio ref={audioRef} src={audioURL} />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: 300,
            height: 300,
            bgcolor: colorTokens.grey[100],
            borderRadius: '50%',
            mb: 2,
            boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            '&:hover': {
              transform: 'scale(1.03)',
              boxShadow: '0 6px 12px rgba(0,0,0,0.15)',
            },
          }}
          onClick={handlePlaySound}
        >
          {isAudioPlaying ? (
            <PauseCircleIcon
              style={{ fontSize: 140, color: theme.palette.primary.main }}
            />
          ) : (
            <PlayCircleFilledWhiteIcon
              style={{ fontSize: 140, color: theme.palette.primary.main }}
            />
          )}
        </Box>
        {isAutoStopped && (
          <Typography
            sx={{ color: 'red', fontSize: 14, mt: 1, textAlign: 'center' }}
          >
            {LIMIT_MINUTES}分を超えたため録音が停止しました。
          </Typography>
        )}

        <Stack direction="row" justifyContent="space-between" spacing={2}>
          <Box p={1} sx={{ flex: 1 }}>
            <Button
              sx={{
                color: '#fff',
                bgcolor: theme.palette.secondary.dark,
                '&:hover': {
                  bgcolor: theme.palette.secondary.main,
                  color: '#fff',
                },
                width: '100%',
                borderRadius: 28,
                padding: '10px 5px',
                whiteSpace: 'nowrap',
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
              }}
              variant="contained"
              onClick={onClearAudio}
              startIcon={<ReplayIcon />}
            >
              録り直し
            </Button>
          </Box>
          <Box p={1} sx={{ flex: 1 }}>
            <Button
              sx={{
                color: '#fff',
                bgcolor: theme.palette.success.main,
                '&:hover': {
                  bgcolor: theme.palette.success.dark,
                  color: '#fff',
                },
                width: '100%',
                borderRadius: 28,
                padding: '10px 5px',
                whiteSpace: 'nowrap',
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
              }}
              variant="contained"
              onClick={onSendAudio}
              startIcon={<CheckIcon />}
            >
              SOAP作成
            </Button>
          </Box>
        </Stack>
      </Stack>
    )
  }

  if (isRecording) {
    return (
      <Stack p={1}>
        <Stack sx={{ width: '100%', alignItems: 'center' }}>
          <Typography
            sx={{
              color: theme.palette.error.main,
              mb: 1,
            }}
          >
            ● 録音中...
          </Typography>
          <Box
            sx={{
              width: 300,
              height: 300,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              borderRadius: '50%',
              backgroundColor: colorTokens.special.recordingBackground,
              boxShadow: '0 4px 12px rgba(101, 154, 210, 0.3)',
              transition: 'all 0.3s ease',
              cursor: 'pointer',
              '&:hover': {
                transform: 'scale(1.03)',
                boxShadow: '0 6px 12px rgba(0,0,0,0.15)',
              },
            }}
            onClick={onStopRecording}
          >
            <MicIcon
              style={{ fontSize: 140, color: colorTokens.special.micIcon }}
            />
            <Typography
              sx={{
                color: colorTokens.special.micIcon,
                mt: 1,
              }}
            >
              {getRecordingButtonText(true, !!isUseNsips)}
            </Typography>
          </Box>
        </Stack>
      </Stack>
    )
  }

  return (
    <Stack p={1}>
      <Stack sx={{ width: '100%', alignItems: 'center' }}>
        <Box
          sx={[
            {
              width: 300,
              height: 300,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              borderRadius: '50%',
              backgroundColor: colorTokens.grey[100],
              boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
              transition: 'all 0.3s ease',
              '&:hover':
                permissionStatus === 'granted'
                  ? {
                      transform: 'scale(1.03)',
                      boxShadow: '0 6px 12px rgba(0,0,0,0.15)',
                    }
                  : {},
            },
            permissionStatus === 'granted'
              ? { cursor: 'pointer' }
              : { cursor: 'default' },
          ]}
          onClick={
            permissionStatus === 'granted' ? onStartRecording : undefined
          }
        >
          <MicIcon
            style={{ fontSize: 140, color: colorTokens.special.micIcon }}
          />
          {permissionStatus === 'granted' && (
            <Typography
              sx={{
                color: colorTokens.special.micIcon,
                mt: 1,
              }}
            >
              {getRecordingButtonText(false, !!isUseNsips)}
            </Typography>
          )}
        </Box>
        {permissionStatus === 'denied' && (
          <Stack gap={2} sx={{ width: '100%', alignItems: 'center' }}>
            <Typography sx={{ color: theme.palette.secondary.dark }}>
              マイクが接続できていません
            </Typography>
            <Button
              sx={{
                color: '#fff',
                width: '100%',
                bgcolor: theme.palette.secondary.dark,
                '&:hover': {
                  bgcolor: theme.palette.secondary.dark,
                  color: '#fff',
                },
                borderRadius: 28,
                padding: '10px 24px',
                boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
              }}
              onClick={() => {
                window.open('/mic-permission-help', '_blank')
              }}
            >
              接続方法を見る
            </Button>
          </Stack>
        )}
      </Stack>
    </Stack>
  )
}
